import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../lib/toast';
import { useAuth } from '../context/AuthContext';
import {
  getOrders, getRestaurant, updateOrderStatus,
  getActiveServiceRequests, acknowledgeServiceRequest,
  completeServiceRequest, cancelServiceRequest
} from '../services/api';
import useOrdersSocket from '../hooks/useOrdersSocket';
import useServiceRequestsSocket from '../hooks/useServiceRequestsSocket';
import useAudioAlert from '../hooks/useAudioAlert';
import OrderDetailsModal from '../components/Orders/OrderDetailsModal';
import OrderCard from '../components/Orders/OrderCard';
import ServiceRequestCard, { SR_PRIORITY_ORDER } from '../components/Orders/ServiceRequestCard';
import SoundToggle from '../components/Kitchen/SoundToggle';
import AppShell from '../components/layout/AppShell';
import './Orders.css';

const SOUND_PREF_KEY = 'orders.soundEnabled';

const Orders = () => {
  const { t, i18n } = useTranslation();
  const { user, restaurants, isAdmin, isManager } = useAuth();
  const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'es' ? 'es-ES' : 'en-US';

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    tabFromUrl === 'history' ? 'history' : tabFromUrl === 'requests' ? 'requests' : 'active'
  );

  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantCurrency, setRestaurantCurrency] = useState(null);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [detailsOrder, setDetailsOrder] = useState(null);

  const [activeFilters, setActiveFilters] = useState({ status: 'all', dateRange: 'today' });
  const [historyFilters, setHistoryFilters] = useState({ dateRange: 'today', search: '' });

  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem(SOUND_PREF_KEY) === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SOUND_PREF_KEY, String(soundEnabled)); } catch {}
  }, [soundEnabled]);
  const { playSound } = useAudioAlert({ enabled: soundEnabled, volume: 0.7 });

  /* --- service requests state --- */
  const [serviceRequests, setServiceRequests] = useState([]);
  const [srLoading, setSrLoading] = useState(false);
  const [srFilter, setSrFilter] = useState('all'); // all | pending | acknowledged
  const [srActionId, setSrActionId] = useState(null);
  const srTimerRef = useRef(null);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'active' || tab === 'history' || tab === 'requests') setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    if (!user) return;
    const paramId = searchParams.get('restaurant');
    if (isManager()) {
      if (restaurants.length === 0) return;
      const managerRestaurantId = restaurants[0].id;
      if (paramId && parseInt(paramId, 10) !== managerRestaurantId) {
        navigate(`/admin/orders?restaurant=${managerRestaurantId}`, { replace: true });
        return;
      }
      setRestaurantId(managerRestaurantId);
    } else if (isAdmin()) {
      const id = paramId ? parseInt(paramId, 10) : (restaurants.length > 0 ? restaurants[0].id : null);
      if (id != null) setRestaurantId(id);
    }
  }, [user, restaurants, searchParams, isAdmin, isManager, navigate]);

  const fetchRestaurantName = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const res = await getRestaurant(restaurantId);
      setRestaurantName(res.data.name || '');
      // Extract currency for proper formatting
      const curr = res.data.currency;
      if (curr) {
        setRestaurantCurrency(curr);
      }
    } catch (e) {
      console.error(e);
    }
  }, [restaurantId]);

  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }
    setPage(1);
    fetchRestaurantName();
  }, [restaurantId, fetchRestaurantName]);

  const setTab = useCallback((tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
    setPage(1);
  }, [searchParams, setSearchParams]);

  const fetchOrders = useCallback(async () => {
    if (!restaurantId) return;
    try {
      setLoading(true);
      const params = { page, limit };
      if (activeTab === 'active') {
        params.statusIn = 'pending,preparing,ready';
        if (activeFilters.status !== 'all') params.status = activeFilters.status;
        if (activeFilters.dateRange !== 'all') params.dateRange = activeFilters.dateRange;
      } else {
        params.status = 'completed';
        if (historyFilters.dateRange !== 'all') params.dateRange = historyFilters.dateRange;
        if (historyFilters.search) params.search = historyFilters.search;
      }
      const res = await getOrders(restaurantId, params);
      const data = res.data;
      setOrders(Array.isArray(data?.orders) ? data.orders : []);
      setTotal(data?.total ?? 0);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
      setTotal(0);
      toast.error(t('orders.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [restaurantId, page, limit, activeTab, activeFilters, historyFilters, t]);

  useEffect(() => {
    if (!restaurantId) return;
    fetchOrders();
  }, [restaurantId, page, activeTab, activeFilters, historyFilters, fetchOrders]);

  const handleSocketUpdate = useCallback(
    ({ type, data }) => {
      if (type === 'new') {
        playSound();
        fetchOrders();
        toast.success(t('orders.newOrderReceived'));
      } else if (type === 'status' && data && data.orderId) {
        if (data.status === 'completed') {
          setOrders((prev) => prev.filter((o) => o.id !== Number(data.orderId)));
          setTotal((prev) => Math.max(0, prev - 1));
          toast.success(t('orders.orderMovedToHistory'));
        } else {
          setOrders((prev) =>
            prev.map((o) =>
              o.id === Number(data.orderId) ? { ...o, status: data.status } : o
            )
          );
        }
      }
    },
    [fetchOrders, t, playSound]
  );
  useOrdersSocket(restaurantId, handleSocketUpdate, activeTab === 'active');

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      setUpdatingId(orderId);
      await updateOrderStatus(orderId, newStatus);
      if (newStatus === 'completed') {
        setOrders((prev) => prev.filter((o) => o.id !== orderId));
        setTotal((prev) => Math.max(0, prev - 1));
        toast.success(t('orders.orderCompleted'));
      } else {
        setOrders((prev) =>
          prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
        );
        toast.success(t('orders.statusUpdated'));
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error(t('orders.errors.statusUpdateFailed'));
    } finally {
      setUpdatingId(null);
    }
  };

  /* ---------- Service Requests ---------- */
  const fetchServiceRequests = useCallback(async () => {
    if (!restaurantId) return;
    try {
      setSrLoading(true);
      const statusParam = srFilter === 'all' ? undefined : srFilter;
      const res = await getActiveServiceRequests(restaurantId, statusParam);
      const list = Array.isArray(res.data?.requests) ? res.data.requests : [];
      // sort by priority then created_at
      list.sort((a, b) => {
        const pa = SR_PRIORITY_ORDER[a.priority] ?? 3;
        const pb = SR_PRIORITY_ORDER[b.priority] ?? 3;
        if (pa !== pb) return pa - pb;
        return new Date(a.created_at) - new Date(b.created_at);
      });
      setServiceRequests(list);
    } catch (err) {
      console.error('Error fetching service requests:', err);
      toast.error(t('orders.serviceRequests.fetchFailed'));
    } finally {
      setSrLoading(false);
    }
  }, [restaurantId, srFilter, t]);

  // Fetch service requests when viewing orders (for inline section) & auto-refresh every 30s
  useEffect(() => {
    if (!restaurantId) return;
    fetchServiceRequests();
    srTimerRef.current = setInterval(fetchServiceRequests, 30000);
    return () => clearInterval(srTimerRef.current);
  }, [restaurantId, fetchServiceRequests]);

  // Real-time socket updates for service requests
  const srSocketCallbacksRef = useRef({});
  srSocketCallbacksRef.current = {
    onNewRequest: (payload) => {
      playSound();
      fetchServiceRequests();
      toast.success(t('orders.serviceRequests.newRequest', { table: payload.tableNumber }));
    },
    onRequestAcknowledged: () => fetchServiceRequests(),
    onRequestCompleted: () => fetchServiceRequests(),
  };
  useServiceRequestsSocket(restaurantId, srSocketCallbacksRef.current);

  const handleAcknowledge = async (requestId) => {
    try {
      setSrActionId(requestId);
      await acknowledgeServiceRequest(requestId);
      setServiceRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, status: 'acknowledged', acknowledged_at: new Date().toISOString() } : r))
      );
      toast.success(t('orders.serviceRequests.requestAcknowledged'));
    } catch (err) {
      console.error('Error acknowledging request:', err);
      toast.error(t('orders.serviceRequests.acknowledgeFailed'));
    } finally {
      setSrActionId(null);
    }
  };

  const handleCompleteSr = async (requestId) => {
    try {
      setSrActionId(requestId);
      await completeServiceRequest(requestId);
      setServiceRequests((prev) => prev.filter((r) => r.id !== requestId));
      toast.success(t('orders.serviceRequests.requestCompleted'));
    } catch (err) {
      console.error('Error completing request:', err);
      toast.error(t('orders.serviceRequests.completeFailed'));
    } finally {
      setSrActionId(null);
    }
  };

  const handleCancelSr = async (requestId) => {
    try {
      setSrActionId(requestId);
      await cancelServiceRequest(requestId);
      setServiceRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      console.error('Error cancelling request:', err);
      toast.error(t('orders.serviceRequests.cancelFailed'));
    } finally {
      setSrActionId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (!user) return null;

  if (isManager() && restaurants.length === 0) {
    return (
      <AppShell maxWidth={false}>
        <div className="orders-panel">
          <p>{t('orders.noRestaurant.managerMessage')}</p>
          <Link to="/manager/dashboard" className="btn btn-primary">{t('orders.backToDashboard')}</Link>
        </div>
      </AppShell>
    );
  }

  if (restaurantId == null) {
    if (isAdmin() && restaurants.length === 0) {
      return (
        <AppShell maxWidth={false}>
          <div className="orders-panel">
            <p>{t('orders.noRestaurant.adminMessage')}</p>
            <Link to="/admin/dashboard" className="btn btn-primary">{t('orders.noRestaurant.goToDashboard')}</Link>
          </div>
        </AppShell>
      );
    }
    return (
      <AppShell maxWidth={false}>
        <div className="orders-panel">
          <div className="loading-container"><div className="loading"></div></div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell maxWidth={false}>
      <div className="orders-page">
      <div className="orders-panel">
        <div className="orders-header">
          <div>
            <h1>{t('orders.title')}</h1>
            <p className="orders-subtitle">{restaurantName}</p>
          </div>
          <div className="orders-actions">
            {isAdmin() && restaurants.length > 1 && (
              <select
                className="form-input orders-restaurant-select"
                value={restaurantId}
                onChange={(e) => {
                  const id = parseInt(e.target.value, 10);
                  setRestaurantId(id);
                  navigate(`/admin/orders?restaurant=${id}`, { replace: true });
                }}
              >
                {restaurants.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            )}
            <SoundToggle variant="light" enabled={soundEnabled} onChange={setSoundEnabled} />
            <Link to={isManager() ? '/manager/dashboard' : '/admin/dashboard'} className="btn btn-secondary">
              {t('orders.backToDashboard')}
            </Link>
            <Link to={`/admin?restaurant=${restaurantId}`} className="btn btn-primary">
              {t('orders.manageMenu')}
            </Link>
          </div>
        </div>

        <div className="orders-tabs">
          <button
            type="button"
            className={`orders-tab-button ${activeTab === 'active' ? 'orders-tab-button--active' : ''}`}
            onClick={() => setTab('active')}
          >
            <span className="orders-tab-icon">🔥</span>
            <span className="orders-tab-label">{t('orders.tabs.active')}</span>
            {activeTab === 'active' && total > 0 && (
              <span className="orders-tab-badge">{total}</span>
            )}
          </button>
          <button
            type="button"
            className={`orders-tab-button ${activeTab === 'history' ? 'orders-tab-button--active' : ''}`}
            onClick={() => setTab('history')}
          >
            <span className="orders-tab-icon">📋</span>
            <span className="orders-tab-label">{t('orders.tabs.history')}</span>
            {activeTab === 'history' && total > 0 && (
              <span className="orders-tab-badge">{total}</span>
            )}
          </button>
          <button
            type="button"
            className={`orders-tab-button ${activeTab === 'requests' ? 'orders-tab-button--active' : ''}`}
            onClick={() => setTab('requests')}
          >
            <span className="orders-tab-icon">🔔</span>
            <span className="orders-tab-label">{t('orders.tabs.serviceRequests')}</span>
            {serviceRequests.length > 0 && (
              <span className="orders-tab-badge orders-tab-badge--sr">{serviceRequests.length}</span>
            )}
          </button>
        </div>

        {/* ===== TAB CONTENT ===== */}
        {activeTab === 'requests' ? (
          /* ---------- Service Requests tab (full) ---------- */
          <>
            <div className="orders-filters sr-filters">
              <div className="filter-group">
                <label htmlFor="sr-status-filter-tab">{t('orders.filters.status')}</label>
                <select
                  id="sr-status-filter-tab"
                  value={srFilter}
                  onChange={(e) => setSrFilter(e.target.value)}
                >
                  <option value="all">{t('orders.serviceRequests.filterAll')}</option>
                  <option value="pending">{t('orders.serviceRequests.filterPending')}</option>
                  <option value="acknowledged">{t('orders.serviceRequests.filterAcknowledged')}</option>
                </select>
              </div>
              <button type="button" className="btn-reset-filters" onClick={() => setSrFilter('all')}>
                {t('orders.filters.reset')}
              </button>
            </div>
            {srLoading ? (
              <div className="loading-container"><div className="loading"></div></div>
            ) : serviceRequests.length === 0 ? (
              <div className="orders-empty">
                <div className="orders-empty-icon">🔔</div>
                <h2>{t('orders.serviceRequests.noRequests')}</h2>
                <p>{t('orders.serviceRequests.noRequestsDesc')}</p>
              </div>
            ) : (
              <div className="sr-list">
                {serviceRequests.map((sr) => (
                  <ServiceRequestCard
                    key={sr.id}
                    request={sr}
                    actionId={srActionId}
                    onAcknowledge={handleAcknowledge}
                    onComplete={handleCompleteSr}
                    onCancel={handleCancelSr}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
          {activeTab === 'active' ? (
              <div className="orders-filters">
                <div className="filter-group">
                  <label htmlFor="orders-status-filter">{t('orders.filters.status')}</label>
                  <select
                    id="orders-status-filter"
                    value={activeFilters.status}
                    onChange={(e) => {
                      setActiveFilters((prev) => ({ ...prev, status: e.target.value }));
                      setPage(1);
                    }}
                  >
                    <option value="all">{t('orders.filters.allStatuses')}</option>
                    <option value="pending">{t('orders.status.pending')}</option>
                    <option value="preparing">{t('orders.status.preparing')}</option>
                    <option value="ready">{t('orders.status.ready')}</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="orders-date-filter">{t('orders.filters.date')}</label>
                  <select
                    id="orders-date-filter"
                    value={activeFilters.dateRange}
                    onChange={(e) => {
                      setActiveFilters((prev) => ({ ...prev, dateRange: e.target.value }));
                      setPage(1);
                    }}
                  >
                    <option value="today">{t('orders.filters.today')}</option>
                    <option value="week">{t('orders.filters.thisWeek')}</option>
                    <option value="month">{t('orders.filters.thisMonth')}</option>
                    <option value="all">{t('orders.filters.allTime')}</option>
                  </select>
                </div>
                <button
                  type="button"
                  className="btn-reset-filters"
                  onClick={() => {
                    setActiveFilters({ status: 'all', dateRange: 'today' });
                    setPage(1);
                  }}
                >
                  {t('orders.filters.reset')}
                </button>
              </div>
            ) : (
              <div className="orders-filters orders-filters-history">
                <div className="filter-group">
                  <label htmlFor="orders-history-date">{t('orders.filters.date')}</label>
                  <select
                    id="orders-history-date"
                    value={historyFilters.dateRange}
                    onChange={(e) => {
                      setHistoryFilters((prev) => ({ ...prev, dateRange: e.target.value }));
                      setPage(1);
                    }}
                  >
                    <option value="today">{t('orders.filters.today')}</option>
                    <option value="week">{t('orders.filters.thisWeek')}</option>
                    <option value="month">{t('orders.filters.thisMonth')}</option>
                    <option value="all">{t('orders.filters.allTime')}</option>
                  </select>
                </div>
                <div className="filter-group filter-group-search">
                  <label htmlFor="orders-history-search">{t('orders.filters.search')}</label>
                  <input
                    id="orders-history-search"
                    type="text"
                    placeholder={t('orders.filters.searchPlaceholder')}
                    value={historyFilters.search}
                    onChange={(e) => {
                      setHistoryFilters((prev) => ({ ...prev, search: e.target.value }));
                      setPage(1);
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="btn-reset-filters"
                  onClick={() => {
                    setHistoryFilters({ dateRange: 'today', search: '' });
                    setPage(1);
                  }}
                >
                  {t('orders.filters.reset')}
                </button>
              </div>
            )}

            {loading ? (
              <div className="loading-container"><div className="loading"></div></div>
            ) : orders.length === 0 ? (
              <div className="orders-empty">
                <div className="orders-empty-icon">
                  {activeTab === 'active' ? '📋' : '✓'}
                </div>
                <h2>
                  {activeTab === 'active' ? t('orders.noActiveOrders') : t('orders.noHistory')}
                </h2>
                <p>
                  {activeTab === 'active' ? t('orders.noActiveOrdersDesc') : t('orders.noHistoryDesc')}
                </p>
              </div>
            ) : (
              <>
                <div className="orders-list">
                  {orders.map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      currency={restaurantCurrency}
                      locale={locale}
                      isActive={activeTab === 'active'}
                      updatingId={updatingId}
                      onStatusChange={handleStatusChange}
                      onViewDetails={setDetailsOrder}
                    />
                  ))}
                </div>
                {total > limit && (
                  <div className="orders-pagination">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      {t('orders.pagination.prev')}
                    </button>
                    <span className="orders-pagination-info">
                      {t('orders.pagination.page', { current: page, total: totalPages })}
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      {t('orders.pagination.next')}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Service Requests section (below orders on Active tab only) */}
            {activeTab === 'active' && (
              <div className="orders-sr-section">
                <h2 className="orders-sr-section-title">
                  <span className="orders-sr-section-icon">🔔</span>
                  {t('orders.tabs.serviceRequests')}
                  {serviceRequests.length > 0 && (
                    <span className="orders-tab-badge orders-tab-badge--sr">{serviceRequests.length}</span>
                  )}
                </h2>
                <div className="orders-filters sr-filters">
                  <div className="filter-group">
                    <label htmlFor="sr-status-filter">{t('orders.filters.status')}</label>
                    <select
                      id="sr-status-filter"
                      value={srFilter}
                      onChange={(e) => setSrFilter(e.target.value)}
                    >
                      <option value="all">{t('orders.serviceRequests.filterAll')}</option>
                      <option value="pending">{t('orders.serviceRequests.filterPending')}</option>
                      <option value="acknowledged">{t('orders.serviceRequests.filterAcknowledged')}</option>
                    </select>
                  </div>
                  <button type="button" className="btn-reset-filters" onClick={() => setSrFilter('all')}>
                    {t('orders.filters.reset')}
                  </button>
                </div>
                {srLoading ? (
                  <div className="loading-container loading-container--sm"><div className="loading"></div></div>
                ) : serviceRequests.length === 0 ? (
                  <div className="orders-sr-empty">
                    <span className="orders-sr-empty-text">{t('orders.serviceRequests.noRequests')}</span>
                  </div>
                ) : (
                  <div className="sr-list">
                    {serviceRequests.map((sr) => (
                      <ServiceRequestCard
                        key={sr.id}
                        request={sr}
                        actionId={srActionId}
                        onAcknowledge={handleAcknowledge}
                        onComplete={handleCompleteSr}
                        onCancel={handleCancelSr}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {detailsOrder && (
        <OrderDetailsModal
          order={detailsOrder}
          onClose={() => setDetailsOrder(null)}
          locale={locale}
          t={t}
          currency={restaurantCurrency}
        />
      )}
    </div>
    </AppShell>
  );
};

export default Orders;
